import React, { useState, useEffect } from 'react';
import { apiService } from '../services/api';
import { formatRate, isPresent, isMissing } from '../utils/format';

const GovernmentLoans = ({ onBackToHome }) => {
  const [selectedLoanType, setSelectedLoanType] = useState('fha');
  const [marketData, setMarketData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Rate calculator state
  const [creditScore, setCreditScore] = useState('620-659');
  const [loanToValue, setLoanToValue] = useState('80-90');
  const [loanPurpose, setLoanPurpose] = useState('purchase');
  const [estimatedRate, setEstimatedRate] = useState(null);
  const [rateLoading, setRateLoading] = useState(false);

  // FHA loan limit lookup state
  const [address, setAddress] = useState('');
  const [loanLimitData, setLoanLimitData] = useState(null);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupError, setLookupError] = useState(null);

  // Get credit score options based on loan type
  const getCreditScoreOptions = () => {
    const baseOptions = [
      { value: '620-659', label: '620-659' },
      { value: '660-699', label: '660-699' },
      { value: '700-739', label: '700-739' },
      { value: '740+', label: '740+' }
    ];

    if (selectedLoanType === 'fha') {
      // FHA allows down to 500 credit score
      return [
        { value: '500-579', label: '500-579 (FHA with 10% down)' },
        { value: '580-619', label: '580-619 (FHA with 3.5% down)' },
        ...baseOptions
      ];
    } else if (selectedLoanType === 'va') {
      // VA has no official minimum but many lenders go down to 500-580
      return [
        { value: '500-579', label: '500-579 (Lender dependent)' },
        { value: '580-619', label: '580-619' },
        ...baseOptions
      ];
    } else if (selectedLoanType === 'usda') {
      // USDA typically requires 640+ but some flexibility
      return [
        { value: '580-619', label: '580-619 (Manual underwriting)' },
        ...baseOptions
      ];
    }

    return baseOptions;
  };

  // Government loan types with their characteristics
  const loanTypes = {
    fha: {
      title: 'FHA Loans',
      icon: '🏠',
      description: 'Federal Housing Administration loans for first-time buyers and lower credit scores',
      minDownPayment: '3.5%',
      maxLTV: '97.75%',
      minCreditScore: 500, // FHA allows 500+ with 10% down, 580+ with 3.5% down
      maxDTI: '57%',
      mipUpfront: '1.75%',
      mipAnnual: '0.55-0.85%',
      benefits: [
        'Lower down payment requirements (3.5%)',
        'More flexible credit guidelines (500+ credit accepted)',
        'Gift funds allowed for down payment',
        'Assumable loans',
        'Higher LTV allowed (up to 97.75%)'
      ],
      considerations: [
        'Mortgage Insurance Premium (MIP) required',
        'Loan limits vary by area',
        'Property must be primary residence',
        'MIP may be permanent on some loans',
        'Cash-out limited to 80% LTV',
        'Scores 500-579 require 10% down payment'
      ]
    },
    va: {
      title: 'VA Loans',
      icon: '🎖️',
      description: 'Department of Veterans Affairs loans for eligible veterans and service members',
      minDownPayment: '0%',
      maxLTV: '100%',
      minCreditScore: 'No minimum (500+ typical)', // VA has no official minimum
      maxDTI: '41% (flexible)',
      mipUpfront: '0%',
      mipAnnual: '0%',
      fundingFee: '2.15-3.30%',
      benefits: [
        'No down payment required',
        'No mortgage insurance',
        'Competitive interest rates',
        'No prepayment penalties',
        'Reusable benefit',
        '100% financing available',
        'Very flexible credit requirements (500+ accepted)'
      ],
      considerations: [
        'VA funding fee required (unless exempt)',
        'Property must meet VA standards',
        'Certificate of Eligibility needed',
        'Primary residence only',
        'Cash-out limited to 90% LTV'
      ]
    },
    usda: {
      title: 'USDA Rural Development',
      icon: '🌾',
      description: 'USDA loans for eligible rural and suburban areas',
      minDownPayment: '0%',
      maxLTV: '100%',
      minCreditScore: 640,
      maxDTI: '41%',
      mipUpfront: '1.00%',
      mipAnnual: '0.35%',
      benefits: [
        'No down payment required',
        'Low mortgage insurance',
        'Below-market interest rates',
        'Flexible credit guidelines',
        '100% financing available'
      ],
      considerations: [
        'Geographic restrictions apply',
        'Income limits based on area median',
        'Property must be in eligible area',
        'Primary residence only',
        'Cash-out limited to 80% LTV'
      ]
    }
  };

  // Government vs conventional spreads - should use live API data
  const getSpreadData = (loanType) => {
    // In production, this should fetch from live API
    // For now, return null to indicate no mock data
    console.log(`Spread data for ${loanType} should be fetched from live API`);
    return null;
  };

  // Rate calculation functions
  const getMaxLTV = (loanType, purpose) => {
    const limits = {
      fha: {
        purchase: 96.5, // 3.5% down
        'rate-term': 97.75,
        'cash-out': 80.0
      },
      va: {
        purchase: 100.0, // No down payment
        'rate-term': 100.0,
        'cash-out': 90.0
      },
      usda: {
        purchase: 100.0, // No down payment
        'rate-term': 100.0,
        'cash-out': 80.0
      }
    };
    return limits[loanType]?.[purpose] || 80;
  };

  const getLTVOptions = () => {
    const maxLTV = getMaxLTV(selectedLoanType, loanPurpose);
    const options = [];
    
    // Base options that apply to all loan types
    if (maxLTV >= 60) options.push({ value: '60-70', label: '60-70% LTV' });
    if (maxLTV >= 70) options.push({ value: '70-80', label: '70-80% LTV' });
    if (maxLTV >= 80) options.push({ value: '80-90', label: '80-90% LTV' });
    if (maxLTV >= 90) options.push({ value: '90-95', label: '90-95% LTV' });
    
    // High LTV options based on loan type and purpose
    if (selectedLoanType === 'fha') {
      if (loanPurpose === 'purchase' || loanPurpose === 'rate-term') {
        options.push({ value: '95-97.75', label: '95-97.75% LTV (FHA Max)' });
      }
    } else if (selectedLoanType === 'va' || selectedLoanType === 'usda') {
      if (loanPurpose === 'purchase' || loanPurpose === 'rate-term') {
        options.push({ value: '95+', label: '95-100% LTV' });
      }
    }
    
    return options;
  };

  const calculateEstimatedRate = async () => {
    // The estimate is the live market rate plus pricing adjustments. Without a
    // live base there is nothing to adjust - this used to start from a
    // hardcoded 6.125 "current market base" behind a fake 800ms delay, so the
    // calculator returned confident quotes built on a constant.
    const baseRate = marketData?.conventional_mbs;

    if (isMissing(baseRate)) {
      setEstimatedRate(null);
      setError('Live mortgage rates are unavailable, so a rate estimate cannot be calculated right now.');
      return;
    }

    setRateLoading(true);
    setError(null);
    try {
      // Credit score adjustments - expanded for government loan flexibility
      const creditAdjustments = {
        '500-579': 1.25, // Significant adjustment for very low scores
        '580-619': 0.75,
        '620-659': 0.25,
        '660-699': 0,
        '700-739': -0.125,
        '740+': -0.25
      };
      
      // LTV adjustments - more granular for government loans
      const ltvAdjustments = {
        '60-70': -0.125,
        '70-80': 0,
        '80-90': 0.125,
        '90-95': 0.25,
        '95+': 0.375,
        '95-97.75': 0.375 // FHA specific
      };
      
      // Loan purpose adjustments
      const purposeAdjustments = {
        'purchase': 0,
        'rate-term': 0.0625, // Slightly higher than purchase
        'cash-out': 0.375 // Significantly higher for cash-out
      };
      
      // Loan type adjustments
      const loanTypeAdjustments = {
        'fha': 0.125, // Slightly higher due to MIP
        'va': -0.25,  // Often better rates
        'usda': -0.125 // Competitive rates
      };
      
      // Check if LTV is valid for loan type and purpose
      const maxLTV = getMaxLTV(selectedLoanType, loanPurpose);
      const currentLTVNum = parseFloat(loanToValue.split('-')[1] || loanToValue.replace('+', '').replace('%', ''));
      
      if (currentLTVNum > maxLTV) {
        // Reset to valid LTV if current selection exceeds maximum
        const validOptions = getLTVOptions();
        if (validOptions.length > 0) {
          setLoanToValue(validOptions[validOptions.length - 1].value);
        }
        setRateLoading(false);
        return;
      }
      
      const creditAdj = creditAdjustments[creditScore] || 0;
      const ltvAdj = ltvAdjustments[loanToValue] || 0;
      const purposeAdj = purposeAdjustments[loanPurpose] || 0;
      const loanAdj = loanTypeAdjustments[selectedLoanType] || 0;
      
      const finalRate = baseRate + creditAdj + ltvAdj + purposeAdj + loanAdj;
      
      // APR calculation includes fees and insurance
      let aprAddOn = 0.15; // Base fees
      if (selectedLoanType === 'fha') {
        aprAddOn += 0.55; // MIP
        if (loanPurpose === 'cash-out') aprAddOn += 0.15; // Additional cash-out fees
      } else if (selectedLoanType === 'va') {
        const fundingFeeRate = loanPurpose === 'purchase' ? 0.215 : 0.305; // VA funding fee
        aprAddOn += fundingFeeRate / 10; // Spread over 10 years for APR calc
      }
      
      const apr = finalRate + aprAddOn;
      
      setEstimatedRate({
        rate: Math.max(finalRate, 3.0), // Floor at 3%
        apr: Math.max(apr, 3.15),
        maxLTV: maxLTV,
        loanPurpose: loanPurpose
      });
    } catch (err) {
      console.error('Rate calculation failed:', err);
    } finally {
      setRateLoading(false);
    }
  };

  const resetCalculator = () => {
    const defaultCreditScore = selectedLoanType === 'usda' ? '620-659' : '580-619';
    setCreditScore(defaultCreditScore);
    setLoanToValue('80-90');
    setLoanPurpose('purchase');
    setEstimatedRate(null);
  };

  // FHA Loan Limit Lookup Function
  const lookupLoanLimit = async () => {
    if (!address.trim()) {
      setLookupError('Please enter an address');
      return;
    }

    setLookupLoading(true);
    setLookupError(null);
    
    try {
      // In production, this should call a real loan limits API
      throw new Error('Loan limit lookup API not implemented yet');
    } catch (err) {
      setLookupError('Loan limit lookup is not available. Please check with your lender for current limits.');
    } finally {
      setLookupLoading(false);
    }
  };

  const fetchMarketData = async () => {
    setLoading(true);
    setError(null);
    try {
      console.log('Fetching live market data for government loans...');
      const response = await apiService.getComprehensiveMarketData();
      
      if (response && response.data) {
        const d = response.data;

        // First candidate that the API actually supplied, else null. `||` was
        // wrong twice over here: it substituted invented spreads (FHA +0.125,
        // VA -0.25, Ginnie Mae 5.85) when a field was absent, and it discarded
        // any genuine spread of exactly 0.
        const value = (...candidates) => {
          for (const candidate of candidates) {
            if (isPresent(candidate)) return Number(candidate);
          }
          return null;
        };

        // Shared across all three programmes - both are agency-level yields.
        const ginneMaeYield = value(d.ginnie_mae_yield, d.mbs_yield);
        const conventionalYield = value(d.conventional_yield, d.mortgage_rate_30y);

        setMarketData({
          fha: {
            currentSpread: value(d.fha_spread),
            avgSpread: value(d.fha_avg_spread),
            trend: d.fha_trend ?? null,
            ginneMaeYield,
            conventionalYield
          },
          va: {
            currentSpread: value(d.va_spread),
            avgSpread: value(d.va_avg_spread),
            trend: d.va_trend ?? null,
            ginneMaeYield,
            conventionalYield
          },
          usda: {
            currentSpread: value(d.usda_spread),
            avgSpread: value(d.usda_avg_spread),
            trend: d.usda_trend ?? null,
            ginneMaeYield,
            conventionalYield
          },
          // Used by the "Key Market Factors" panel.
          fed_funds_rate: value(d.fed_funds_rate),
          treasury_10y: value(d.treasury_10y),
          ginnie_mae_30y: ginneMaeYield,
          conventional_mbs: conventionalYield
        });
      } else {
        throw new Error('Invalid response format from market data API');
      }
    } catch (err) {
      console.error('Failed to fetch government loans market data:', err);
      setError(`Failed to load government loans data: ${err.message}. Please try refreshing the page.`);
      setMarketData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMarketData();
  }, []);

  const getCurrentLoan = () => loanTypes[selectedLoanType];
  const getCurrentSpread = () => marketData?.[selectedLoanType];

  const formatSpread = (spread) => {
    const sign = spread >= 0 ? '+' : '';
    return `${sign}${(spread * 100).toFixed(2)}%`;
  };

  const getSpreadColor = (spread) => {
    if (spread < -0.1) return 'text-green-600';
    if (spread > 0.1) return 'text-red-600';
    return 'text-gray-600';
  };

  const getTrendIcon = (trend) => {
    switch (trend) {
      case 'tightening': return '📈';
      case 'improving': return '📉';
      default: return '➡️';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <button
              onClick={onBackToHome}
              className="flex items-center space-x-2 text-blue-600 hover:text-blue-700"
            >
              <span>←</span>
              <span>Back to Home</span>
            </button>
            <div className="text-center">
              <h1 className="text-3xl font-bold text-gray-900">Government-Backed Loans</h1>
              <p className="text-lg text-gray-600 mt-2">
                FHA, VA, and USDA loan rates and market analysis
              </p>
            </div>
            <div className="w-24"></div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Loan Type Tabs */}
        <div className="mb-8">
          <div className="flex justify-center space-x-4">
            {Object.entries(loanTypes).map(([key, loan]) => (
              <button
                key={key}
                onClick={() => {
                  setSelectedLoanType(key);
                  // Reset credit score to appropriate default for new loan type
                  const newDefaultCredit = key === 'usda' ? '620-659' : '580-619';
                  setCreditScore(newDefaultCredit);
                  setEstimatedRate(null); // Clear results when loan type changes
                }}
                className={`px-6 py-3 rounded-lg font-semibold transition-all duration-200 ${
                  selectedLoanType === key
                    ? 'bg-blue-600 text-white shadow-md'
                    : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'
                }`}
              >
                <span className="text-xl mr-2">{loan.icon}</span>
                {loan.title}
              </button>
            ))}
          </div>
        </div>

        {/* Current Spread Alert */}
        {!loading && marketData && (
          <div className="mb-8 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <span className="text-2xl">📊</span>
                <div>
                  <h3 className="font-semibold text-blue-900">Current Market Spread</h3>
                  <p className="text-sm text-blue-700">
                    {getCurrentLoan().title} vs. Conventional Mortgage Rates
                  </p>
                </div>
              </div>
              <div className="text-right">
                <div className={`text-2xl font-bold ${getSpreadColor(getCurrentSpread()?.currentSpread)}`}>
                  {formatSpread(getCurrentSpread()?.currentSpread || 0)}
                </div>
                <div className="text-sm text-gray-600 flex items-center space-x-1">
                  <span>{getTrendIcon(getCurrentSpread()?.trend)}</span>
                  <span>{getCurrentSpread()?.trend}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Info Panel */}
          <div className="lg:col-span-2 space-y-6">
            {/* Loan Overview */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-center space-x-3 mb-6">
                <span className="text-3xl">{getCurrentLoan().icon}</span>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">{getCurrentLoan().title}</h2>
                  <p className="text-gray-600">{getCurrentLoan().description}</p>
                </div>
              </div>

              {/* Key Features Grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="text-center p-3 bg-gray-50 rounded-lg">
                  <div className="text-sm text-gray-500">Min Down Payment</div>
                  <div className="text-lg font-bold text-gray-900">{getCurrentLoan().minDownPayment}</div>
                </div>
                <div className="text-center p-3 bg-gray-50 rounded-lg">
                  <div className="text-sm text-gray-500">Min Credit Score</div>
                  <div className="text-lg font-bold text-gray-900">{getCurrentLoan().minCreditScore}</div>
                </div>
                <div className="text-center p-3 bg-gray-50 rounded-lg">
                  <div className="text-sm text-gray-500">Max DTI</div>
                  <div className="text-lg font-bold text-gray-900">{getCurrentLoan().maxDTI}</div>
                </div>
                <div className="text-center p-3 bg-gray-50 rounded-lg">
                  <div className="text-sm text-gray-500">
                    {selectedLoanType === 'va' ? 'Funding Fee' : 'Annual MI/Fee'}
                  </div>
                  <div className="text-lg font-bold text-gray-900">
                    {selectedLoanType === 'va' ? getCurrentLoan().fundingFee : getCurrentLoan().mipAnnual}
                  </div>
                </div>
              </div>

              {/* Benefits and Considerations */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="font-semibold text-green-800 mb-3 flex items-center">
                    <span className="mr-2">✅</span>
                    Key Benefits
                  </h3>
                  <ul className="space-y-2">
                    {getCurrentLoan().benefits.map((benefit, index) => (
                      <li key={index} className="flex items-start space-x-2 text-sm">
                        <span className="text-green-600 mt-1">•</span>
                        <span className="text-gray-700">{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <h3 className="font-semibold text-orange-800 mb-3 flex items-center">
                    <span className="mr-2">⚠️</span>
                    Important Considerations
                  </h3>
                  <ul className="space-y-2">
                    {getCurrentLoan().considerations.map((consideration, index) => (
                      <li key={index} className="flex items-start space-x-2 text-sm">
                        <span className="text-orange-600 mt-1">•</span>
                        <span className="text-gray-700">{consideration}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

            {/* Market Analysis */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                <span className="mr-2">📈</span>
                Government vs. Conventional Spread Analysis
              </h3>

              {/* Educational Explanation */}
              <div className="mb-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200">
                <h4 className="font-semibold text-blue-900 mb-3 flex items-center">
                  <span className="mr-2">📚</span>
                  Understanding Yield Spreads
                </h4>
                <div className="text-sm text-blue-800 space-y-2">
                  <p>
                    <strong>What is a yield spread?</strong> The difference between government loan rates (Ginnie Mae MBS) and conventional loan rates (Fannie/Freddie MBS).
                  </p>
                  <div className="grid md:grid-cols-2 gap-4 mt-3">
                    <div className="bg-green-100 p-3 rounded border border-green-300">
                      <div className="font-medium text-green-800">✅ Negative Spread (Good for Borrowers)</div>
                      <div className="text-xs text-green-700 mt-1">
                        Government loans offer better rates than conventional loans. You save money compared to conventional financing.
                      </div>
                    </div>
                    <div className="bg-orange-100 p-3 rounded border border-orange-300">
                      <div className="font-medium text-orange-800">⚠️ Positive Spread (Higher Cost)</div>
                      <div className="text-xs text-orange-700 mt-1">
                        Government loans cost more than conventional loans. Consider if benefits (low down payment, flexible credit) justify the premium.
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              {!loading && marketData && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div className="flex justify-between items-center p-4 bg-gray-50 rounded-lg">
                      <span className="text-gray-600">Ginnie Mae MBS Yield</span>
                      <span className="font-bold">{getCurrentSpread()?.ginneMaeYield.toFixed(3)}%</span>
                    </div>
                    <div className="flex justify-between items-center p-4 bg-gray-50 rounded-lg">
                      <span className="text-gray-600">Conventional MBS Yield</span>
                      <span className="font-bold">{getCurrentSpread()?.conventionalYield.toFixed(3)}%</span>
                    </div>
                    <div className="flex justify-between items-center p-4 bg-blue-50 rounded-lg border border-blue-200">
                      <span className="text-blue-800 font-medium">Current Spread</span>
                      <span className={`font-bold ${getSpreadColor(getCurrentSpread()?.currentSpread)}`}>
                        {formatSpread(getCurrentSpread()?.currentSpread)}
                      </span>
                    </div>
                  </div>
                  
                  <div className="space-y-4">
                    <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                      <h4 className="font-medium text-green-800 mb-2">Current Market Conditions</h4>
                      <p className="text-sm text-green-700 mb-3">
                        {getCurrentSpread()?.currentSpread < 0 
                          ? `${getCurrentLoan().title} currently offer rates about ${Math.abs(getCurrentSpread()?.currentSpread * 100).toFixed(2)} basis points BETTER than conventional loans.`
                          : getCurrentSpread()?.currentSpread > 0
                          ? `${getCurrentLoan().title} currently price about ${(getCurrentSpread()?.currentSpread * 100).toFixed(2)} basis points HIGHER than conventional loans.`
                          : `${getCurrentLoan().title} are currently priced very similarly to conventional loans.`
                        }
                      </p>
                      <div className="text-xs text-green-600 bg-green-100 p-2 rounded">
                        <strong>💡 Borrower Impact:</strong> 
                        {getCurrentSpread()?.currentSpread < 0 
                          ? ` This is a GOOD time for government loans - you're getting better rates than conventional financing, plus the benefits of lower down payments and flexible credit requirements.`
                          : getCurrentSpread()?.currentSpread > 0
                          ? ` Consider if government loan benefits (low down payment, flexible credit) justify paying ${(getCurrentSpread()?.currentSpread * 100).toFixed(2)} basis points more than conventional loans.`
                          : ` Government loans offer similar rates to conventional loans, making them attractive for their low down payment and flexible credit benefits.`
                        }
                      </div>
                    </div>
                    
                    <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                      <h4 className="font-medium text-blue-800 mb-2">Historical Context</h4>
                      <p className="text-sm text-blue-700">
                        The average spread over the past year has been {formatSpread(getCurrentSpread()?.avgSpread)}.
                        Current conditions are {getCurrentSpread()?.trend} relative to recent trends.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Rate Calculator */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center">
                <span className="mr-2">🧮</span>
                Quick Rate Estimate
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Loan Purpose</label>
                  <select 
                    value={loanPurpose}
                    onChange={(e) => {
                      setLoanPurpose(e.target.value);
                      setEstimatedRate(null); // Clear results when purpose changes
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="purchase">Purchase</option>
                    <option value="rate-term">Rate & Term Refinance</option>
                    <option value="cash-out">Cash-Out Refinance</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Credit Score
                    {selectedLoanType === 'fha' && (
                      <span className="text-xs text-blue-600 ml-1">(500+ accepted with higher down payment)</span>
                    )}
                    {selectedLoanType === 'va' && (
                      <span className="text-xs text-blue-600 ml-1">(No VA minimum - lender dependent)</span>
                    )}
                  </label>
                  <select 
                    value={creditScore}
                    onChange={(e) => setCreditScore(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {getCreditScoreOptions().map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  {creditScore === '500-579' && selectedLoanType === 'fha' && (
                    <div className="mt-1 text-xs text-orange-600">
                      ⚠️ FHA requires 10% down payment for credit scores 500-579
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Loan-to-Value (LTV)
                    <span className="text-xs text-gray-500 ml-1">
                      (Max: {getMaxLTV(selectedLoanType, loanPurpose)}% for {selectedLoanType.toUpperCase()} {loanPurpose === 'cash-out' ? 'cash-out' : loanPurpose})
                    </span>
                  </label>
                  <select 
                    value={loanToValue}
                    onChange={(e) => setLoanToValue(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {getLTVOptions().map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  {loanPurpose === 'cash-out' && (
                    <div className="mt-1 text-xs text-orange-600">
                      ⚠️ Cash-out limited to 80% LTV for all government loan programs
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="flex space-x-2">
                  <button
                    onClick={calculateEstimatedRate}
                    disabled={rateLoading}
                    className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors disabled:bg-blue-400 disabled:cursor-not-allowed"
                  >
                    {rateLoading ? (
                      <div className="flex items-center justify-center">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Running...
                      </div>
                    ) : (
                      'Run Scenario'
                    )}
                  </button>
                  <button
                    onClick={resetCalculator}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Reset
                  </button>
                </div>

                {/* Results */}
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="text-center">
                    <div className="text-sm text-blue-600">Estimated Rate</div>
                    {estimatedRate ? (
                      <>
                        <div className="text-2xl font-bold text-blue-800">
                          {estimatedRate.rate.toFixed(3)}%
                        </div>
                        <div className="text-xs text-blue-600 mt-1">
                          APR: {estimatedRate.apr.toFixed(3)}%
                        </div>
                        <div className="text-xs text-gray-500 mt-2 space-y-1">
                          <div>{selectedLoanType.toUpperCase()} {loanPurpose} loan</div>
                          <div>{creditScore} credit score, {loanToValue} LTV</div>
                          {loanPurpose === 'cash-out' && (
                            <div className="text-orange-600 font-medium">
                              Cash-out rates typically 0.375% higher
                            </div>
                          )}
                          {selectedLoanType === 'fha' && parseFloat(loanToValue.split('-')[1] || '80') > 95 && (
                            <div className="text-green-600 font-medium">
                              Using FHA maximum LTV of 97.75%
                            </div>
                          )}
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="text-xl font-medium text-gray-500">---%</div>
                        <div className="text-xs text-gray-500 mt-1">Click "Run Scenario" for estimate</div>
                      </>
                    )}
                  </div>
                </div>

                {/* Disclaimer */}
                <div className="text-xs text-gray-500 leading-relaxed">
                  <strong>Disclaimer:</strong> This is an educational estimate only. Actual rates depend on many factors including specific lender, property type, debt-to-income ratio, and current market conditions. Contact a licensed lender for accurate pricing.
                </div>

                {/* Loan Purpose Information */}
                <div className="bg-gray-50 rounded-lg p-3">
                  <h4 className="text-xs font-semibold text-gray-700 mb-2">📚 Loan Purpose Guide:</h4>
                  <div className="text-xs text-gray-600 space-y-1">
                    <div><strong>Purchase:</strong> Buying a home (highest LTV available)</div>
                    <div><strong>Rate & Term:</strong> Refinancing to lower rate or change terms</div>
                    <div><strong>Cash-Out:</strong> Refinancing for more than owed to get cash</div>
                  </div>
                  {loanPurpose === 'cash-out' && (
                    <div className="mt-2 p-2 bg-orange-50 rounded border border-orange-200">
                      <div className="text-xs text-orange-700">
                        <strong>⚠️ Cash-Out Limits:</strong><br/>
                        • FHA: Max 80% LTV (vs 97.75% for purchase)<br/>
                        • VA: Max 90% LTV (vs 100% for purchase)<br/>
                        • USDA: Max 80% LTV (vs 100% for purchase)<br/>
                        <em className="text-orange-600">Plus higher rates due to increased risk</em>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Market Factors */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center">
                <span className="mr-2">📊</span>
                Key Market Factors
              </h3>
              {/* These four were written into the markup as fixed strings
                  (5.25% / 4.20% / 5.85% / 5.98%) and never changed, whatever the
                  market did. Ginnie Mae and conventional MBS coupons have no
                  source in the API yet, so they report as unavailable. */}
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Fed Funds Rate</span>
                  <span className="font-medium">{formatRate(marketData?.fed_funds_rate)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">10Y Treasury</span>
                  <span className="font-medium">{formatRate(marketData?.treasury_10y)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Ginnie Mae 30Y</span>
                  <span className="font-medium">{formatRate(marketData?.ginnie_mae_30y)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Conventional MBS</span>
                  <span className="font-medium">{formatRate(marketData?.conventional_mbs)}</span>
                </div>
              </div>
            </div>

            {/* FHA Loan Limit Lookup - Only show for FHA */}
            {selectedLoanType === 'fha' && (
              <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="font-semibold text-gray-900 mb-4 flex items-center">
                  <span className="mr-2">🏘️</span>
                  FHA Loan Limit Lookup
                </h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Property Address
                    </label>
                    <input
                      type="text"
                      value={address}
                      onChange={(e) => setAddress(e.target.value)}
                      placeholder="Enter street address, city, state"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  
                  <button
                    onClick={lookupLoanLimit}
                    disabled={lookupLoading || !address.trim()}
                    className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors disabled:bg-blue-400 disabled:cursor-not-allowed"
                  >
                    {lookupLoading ? (
                      <div className="flex items-center justify-center">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Looking up limits...
                      </div>
                    ) : (
                      'Check Loan Limits'
                    )}
                  </button>

                  {lookupError && (
                    <div className="text-red-600 text-sm">{lookupError}</div>
                  )}

                  {loanLimitData && (
                    <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                      <h4 className="font-medium text-blue-900 mb-3">
                        {loanLimitData.county} Loan Limits
                      </h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-blue-700">1 Unit:</span>
                          <span className="font-medium text-blue-900">
                            ${loanLimitData.oneUnit.toLocaleString()}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-blue-700">2 Unit:</span>
                          <span className="font-medium text-blue-900">
                            ${loanLimitData.twoUnit.toLocaleString()}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-blue-700">3 Unit:</span>
                          <span className="font-medium text-blue-900">
                            ${loanLimitData.threeUnit.toLocaleString()}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-blue-700">4 Unit:</span>
                          <span className="font-medium text-blue-900">
                            ${loanLimitData.fourUnit.toLocaleString()}
                          </span>
                        </div>
                      </div>
                      
                      <div className="mt-3 p-2 bg-blue-100 rounded border border-blue-300">
                        <div className="text-xs text-blue-800">
                          <strong>
                            {loanLimitData.isHighCost ? '🏙️ High-Cost Area' : '🏞️ Baseline Area'}
                          </strong>
                          <br />
                          {loanLimitData.isHighCost 
                            ? `This area has higher loan limits due to elevated home prices compared to the national baseline of $${loanLimitData.baseLimit.toLocaleString()}.`
                            : `This area uses the national baseline loan limit of $${loanLimitData.baseLimit.toLocaleString()}.`
                          }
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="text-xs text-gray-500">
                    <strong>💡 Why this matters:</strong> FHA loan limits vary by county based on local home prices. Your loan amount cannot exceed these limits for FHA financing.
                  </div>
                </div>
              </div>
            )}

            {/* Eligibility Check */}
            <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-lg border border-green-200 p-6">
              <h3 className="font-semibold text-gray-900 mb-3 flex items-center">
                <span className="mr-2">✓</span>
                Eligibility Quick Check
              </h3>
              <div className="space-y-2 text-sm">
                {selectedLoanType === 'fha' && (
                  <>
                    <div className="flex items-center space-x-2">
                      <span className="text-green-600">✓</span>
                      <span>Credit score 500+ (580+ for 3.5% down, 500-579 for 10% down)</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="text-green-600">✓</span>
                      <span>Primary residence only</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="text-green-600">✓</span>
                      <span>Meet FHA loan limits for your area</span>
                    </div>
                  </>
                )}
                {selectedLoanType === 'va' && (
                  <>
                    <div className="flex items-center space-x-2">
                      <span className="text-green-600">✓</span>
                      <span>Eligible veteran or service member</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="text-green-600">✓</span>
                      <span>Certificate of Eligibility (COE)</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="text-green-600">✓</span>
                      <span>Property meets VA requirements</span>
                    </div>
                  </>
                )}
                {selectedLoanType === 'usda' && (
                  <>
                    <div className="flex items-center space-x-2">
                      <span className="text-green-600">✓</span>
                      <span>Property in USDA eligible area</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="text-green-600">✓</span>
                      <span>Income within area limits</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="text-green-600">✓</span>
                      <span>Primary residence only</span>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GovernmentLoans;
